import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { SelectWithCreate } from '../../components/ui/select-with-create';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  ArrowLeft, 
  Server, 
  Edit, 
  Trash2,
  Cpu,
  HardDrive,
  Network,
  Shield,
  Package,
  Database,
  Activity,
  Users,
  FolderOpen,
  Printer,
  Wrench,
  UserCog,
  CalendarClock,
  // Icons for detail fields
  Monitor,
  Box,
  Tag,
  Hash,
  AlertTriangle,
  Globe,
  Wifi,
  Lock,
  Cloud,
  Layers,
  MapPin,
  MemoryStick,
  CircuitBoard,
  Gauge,
  Building2,
  Settings,
  CheckCircle,
  Clock,
  DollarSign,
  // Sorting icons
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Play,
  XCircle,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../../hooks/usePermissions';
import { ServerBackupAssignments } from '../../components/backup/ServerBackupAssignments';

// ============ Windows Service/Role Mappings ============

// Service Status (ServiceControllerStatus)
const SERVICE_STATUS_MAP = {
  '1': { label: 'Detenido', badge: 'badge-error' },
  '2': { label: 'Iniciando', badge: 'badge-warning' },
  '3': { label: 'Deteniendo', badge: 'badge-warning' },
  '4': { label: 'En ejecución', badge: 'badge-success' },
  '5': { label: 'Reanudando', badge: 'badge-warning' },
  '6': { label: 'Pausando', badge: 'badge-warning' },
  '7': { label: 'Pausado', badge: 'badge-warning' },
};

// Service StartType (ServiceStartMode)
const SERVICE_START_TYPE_MAP = {
  '0': { label: 'Arranque', badge: 'badge-default' },
  '1': { label: 'Sistema', badge: 'badge-default' },
  '2': { label: 'Automático', badge: 'badge-success' },
  '3': { label: 'Manual', badge: 'badge-warning' },
  '4': { label: 'Deshabilitado', badge: 'badge-error' },
};

// Role InstallState
const ROLE_INSTALL_STATE_MAP = {
  '0': { label: 'Disponible', badge: 'badge-outline' },
  '1': { label: 'Instalado', badge: 'badge-success' },
  '2': { label: 'Removido', badge: 'badge-default' },
  '3': { label: 'Pendiente Instalar', badge: 'badge-warning' },
  '4': { label: 'Pendiente Desinstalar', badge: 'badge-warning' },
  // Por si viene como string del enum
  'Installed': { label: 'Instalado', badge: 'badge-success' },
  'Available': { label: 'Disponible', badge: 'badge-outline' },
  'Removed': { label: 'Removido', badge: 'badge-default' },
  'InstallPending': { label: 'Pendiente Instalar', badge: 'badge-warning' },
  'UninstallPending': { label: 'Pendiente Desinstalar', badge: 'badge-warning' },
  // Para Windows Client (Get-WindowsOptionalFeature)
  'Enabled': { label: 'Habilitado', badge: 'badge-success' },
  'Disabled': { label: 'Deshabilitado', badge: 'badge-outline' },
  'DisabledWithPayloadRemoved': { label: 'Removido', badge: 'badge-default' },
};

// Helper functions
const getServiceStatus = (status) => {
  const mapped = SERVICE_STATUS_MAP[String(status)];
  return mapped || { label: status || '-', badge: 'badge-default' };
};

const getServiceStartType = (startType) => {
  const mapped = SERVICE_START_TYPE_MAP[String(startType)];
  return mapped || { label: startType || '-', badge: 'badge-default' };
};

const getRoleInstallState = (state) => {
  const mapped = ROLE_INSTALL_STATE_MAP[String(state)];
  return mapped || { label: state || '-', badge: 'badge-default' };
};

// Component for detail rows with icon
const DetailRow = ({ icon: Icon, label, value, mono = false, highlight = false }) => (
  <div className="flex justify-between items-center py-2.5 border-b border-border/30 last:border-0">
    <span className="text-sm text-muted-foreground flex items-center gap-2">
      {Icon && <Icon className="h-4 w-4 text-primary/70" />}
      {label}
    </span>
    <span className={`text-sm ${mono ? 'font-mono' : ''} ${highlight ? 'text-primary' : ''}`}>
      {value || '-'}
    </span>
  </div>
);

// ============ SCHEDULED TASKS TAB COMPONENT ============
const ScheduledTasksTab = ({ tasks }) => {
  const [search, setSearch] = useState('');
  const [showOnlyRedFlags, setShowOnlyRedFlags] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = !search || 
      task.task_name?.toLowerCase().includes(search.toLowerCase()) ||
      task.principal?.toLowerCase().includes(search.toLowerCase()) ||
      task.author?.toLowerCase().includes(search.toLowerCase());
    const matchesRedFlag = !showOnlyRedFlags || task.is_red_flag;
    return matchesSearch && matchesRedFlag;
  });

  const redFlagCount = tasks.filter(t => t.is_red_flag).length;

  const getStateStyle = (state) => {
    const styles = {
      'Ready': 'bg-green-500/10 text-green-400 border-green-500/30',
      'Running': 'bg-blue-500/10 text-blue-400 border-blue-500/30',
      'Disabled': 'bg-gray-500/10 text-gray-400 border-gray-500/30',
      'Queued': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
      'Enabled': 'bg-green-500/10 text-green-400 border-green-500/30',
      'Loaded': 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    };
    return styles[state] || 'bg-gray-500/10 text-gray-400 border-gray-500/30';
  };

  // last_result codes - Windows HRESULT
  const formatLastResult = (result) => {
    if (result == null) return { text: '—', style: 'text-muted-foreground' };
    if (result === 0) return { text: '✓ OK', style: 'text-green-400' };
    // Informational codes (0x4xxxx range)
    if (result === 267009) return { text: 'No ejecutada', style: 'text-blue-400' }; // SCHED_S_TASK_HAS_NOT_RUN
    if (result === 267011) return { text: 'Terminada', style: 'text-blue-400' }; // SCHED_S_TASK_TERMINATED
    if (result === 267014) return { text: 'Logout', style: 'text-yellow-400' }; // User logged out
    // Error codes - show hex
    const hex = '0x' + (result >>> 0).toString(16).toUpperCase().padStart(8, '0');
    return { text: `${result} (${hex})`, style: 'text-red-400' };
  };

  // Detect sentinel value for "never ran" (Windows uses 1999-11-30)
  const formatDateTime = (dt) => {
    if (!dt) return '—';
    const date = new Date(dt);
    if (date.getFullYear() < 2000) return 'Nunca';
    return date.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
  };

  const parseJson = (jsonStr) => {
    if (!jsonStr) return null;
    try {
      return typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr;
    } catch {
      return null;
    }
  };

  // Parse red flag reasons (CSV format)
  const parseRedFlagReasons = (reasons) => {
    if (!reasons) return [];
    return reasons.split(',').map(r => r.trim()).filter(Boolean);
  };

  const formatRedFlagReason = (reason) => {
    const labels = {
      'encoded_command': 'Comando codificado (Base64)',
      'privileged_in_temp_path': 'Privilegiado en ruta temporal',
      'remote_download_exec': 'Descarga y ejecución remota',
      'disabled_with_failure': 'Deshabilitada con error',
    };
    return labels[reason] || reason;
  };

  const openDetail = (task) => {
    setSelectedTask(task);
    setDetailModalOpen(true);
  };

  return (
    <>
      <Card className="nerve-card">
        <CardHeader className="border-b border-border pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-primary" />
              Scheduled Tasks ({filteredTasks.length})
              {redFlagCount > 0 && (
                <span className="flex items-center gap-1 text-red-400 text-xs font-normal ml-2">
                  <AlertTriangle className="h-3 w-3" />
                  {redFlagCount} alertas
                </span>
              )}
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar tarea..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-8 w-48 text-sm"
                />
              </div>
              {redFlagCount > 0 && (
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-red-flags"
                    checked={showOnlyRedFlags}
                    onCheckedChange={setShowOnlyRedFlags}
                  />
                  <Label htmlFor="show-red-flags" className="text-xs cursor-pointer text-red-400">
                    Solo alertas
                  </Label>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredTasks.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-border">
                    <TableHead className="text-xs w-8"></TableHead>
                    <TableHead className="text-xs">Tarea</TableHead>
                    <TableHead className="text-xs">Estado</TableHead>
                    <TableHead className="text-xs">Principal</TableHead>
                    <TableHead className="text-xs">Última Ejecución</TableHead>
                    <TableHead className="text-xs">Resultado</TableHead>
                    <TableHead className="text-xs">Próxima Ejecución</TableHead>
                    <TableHead className="text-xs w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTasks.map((task) => {
                    const resultInfo = formatLastResult(task.last_result);
                    const reasons = parseRedFlagReasons(task.red_flag_reasons);
                    const tooltipText = reasons.map(r => formatRedFlagReason(r)).join('\n');
                    
                    return (
                    <TableRow 
                      key={task.id} 
                      className={`hover:bg-muted/30 border-border/50 ${task.is_red_flag ? 'bg-red-500/5' : ''}`}
                    >
                      <TableCell className="py-2">
                        {task.is_red_flag && (
                          <AlertTriangle 
                            className="h-4 w-4 text-red-500 cursor-help" 
                            title={tooltipText}
                          />
                        )}
                      </TableCell>
                      <TableCell className="py-2">
                        <div className="max-w-[250px]">
                          <div className="font-medium text-sm truncate" title={task.task_name}>
                            {task.task_name}
                          </div>
                          {task.task_path && task.task_path !== '\\' && (
                            <div className="text-xs text-muted-foreground truncate" title={task.task_path}>
                              {task.task_path}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getStateStyle(task.state)}`}>
                          {task.state || '—'}
                        </span>
                      </TableCell>
                      <TableCell className="py-2">
                        <span className="text-xs font-mono">{task.principal || '—'}</span>
                      </TableCell>
                      <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDateTime(task.last_run_time)}
                      </TableCell>
                      <TableCell className="py-2">
                        <span className={`text-xs font-mono ${resultInfo.style}`}>
                          {resultInfo.text}
                        </span>
                      </TableCell>
                      <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDateTime(task.next_run_time)}
                      </TableCell>
                      <TableCell className="py-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openDetail(task)}
                          title="Ver detalle"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8">
              <CalendarClock className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">
                {tasks.length > 0 ? 'Sin resultados para el filtro' : 'Sin tareas programadas registradas'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Task Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b border-border pb-3">
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-primary" />
              Detalle de Tarea
              {selectedTask?.is_red_flag && (
                <AlertTriangle className="h-4 w-4 text-red-500 ml-2" />
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTask && (
            <div className="flex-1 overflow-y-auto space-y-4 p-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <label className="text-xs text-muted-foreground">Nombre</label>
                  <p className="font-medium">{selectedTask.task_name}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Ruta</label>
                  <p className="text-sm font-mono">{selectedTask.task_path || '—'}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Estado</label>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getStateStyle(selectedTask.state)}`}>
                    {selectedTask.state || '—'}
                  </span>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Principal</label>
                  <p className="font-mono text-sm">{selectedTask.principal || '—'}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Autor</label>
                  <p className="text-sm">{selectedTask.author || '—'}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Último Resultado</label>
                  {(() => {
                    const resultInfo = formatLastResult(selectedTask.last_result);
                    return <p className={`font-mono ${resultInfo.style}`}>{resultInfo.text}</p>;
                  })()}
                </div>
              </div>

              {/* Red Flag Warning */}
              {selectedTask.is_red_flag && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-red-400 font-medium mb-2">
                    <AlertTriangle className="h-4 w-4" />
                    Alerta de Seguridad
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {parseRedFlagReasons(selectedTask.red_flag_reasons).map((reason, idx) => (
                      <span key={idx} className="inline-flex items-center px-2 py-1 rounded text-xs bg-red-500/20 text-red-300 border border-red-500/30">
                        {formatRedFlagReason(reason)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedTask.description && (
                <div className="p-4 bg-muted/30 rounded-lg">
                  <label className="text-xs text-muted-foreground">Descripción</label>
                  <p className="text-sm mt-1">{selectedTask.description}</p>
                </div>
              )}

              {/* Actions */}
              {parseJson(selectedTask.actions_json) && (
                <div className="p-4 bg-muted/30 rounded-lg">
                  <label className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                    <Play className="h-3 w-3" /> Acciones
                  </label>
                  <div className="space-y-2">
                    {parseJson(selectedTask.actions_json).map((action, idx) => (
                      <div key={idx} className="p-2 bg-background/50 rounded text-xs font-mono">
                        <div><span className="text-muted-foreground">Execute:</span> {action.execute}</div>
                        {action.arguments && <div><span className="text-muted-foreground">Args:</span> {action.arguments}</div>}
                        {action.workdir && <div><span className="text-muted-foreground">WorkDir:</span> {action.workdir}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Triggers */}
              {parseJson(selectedTask.triggers_json) && (
                <div className="p-4 bg-muted/30 rounded-lg">
                  <label className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                    <Clock className="h-3 w-3" /> Triggers
                  </label>
                  <div className="space-y-2">
                    {parseJson(selectedTask.triggers_json).map((trigger, idx) => (
                      <div key={idx} className="p-2 bg-background/50 rounded text-xs">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${trigger.enabled ? 'bg-green-500/10 text-green-400' : 'bg-gray-500/10 text-gray-400'}`}>
                          {trigger.type?.replace('MSFT_Task', '').replace('Trigger', '')}
                        </span>
                        {trigger.days_interval && <span className="ml-2 text-muted-foreground">Cada {trigger.days_interval} día(s)</span>}
                        {trigger.start && <span className="ml-2 text-muted-foreground">Desde: {formatDateTime(trigger.start)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Timing Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <label className="text-xs text-muted-foreground">Última Ejecución</label>
                  <p className="text-sm">{formatDateTime(selectedTask.last_run_time)}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Próxima Ejecución</label>
                  <p className="text-sm">{formatDateTime(selectedTask.next_run_time)}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Capturado</label>
                  <p className="text-sm">{formatDateTime(selectedTask.captured_at)}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const ServerDetail = () => {
  const { hostname } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { canUpdate, canDelete } = usePermissions('assets', 'servers');
  
  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [formOptions, setFormOptions] = useState({ tipos: [], estados: [], sites: [], os: [], clouds: [], criticities: [], restart_windows: [] });
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // ===== Tab Filters & Sorting States =====
  // Software
  const [softwareSearch, setSoftwareSearch] = useState('');
  const [softwareSort, setSoftwareSort] = useState({ field: 'name', dir: 'asc' });
  const [softwarePage, setSoftwarePage] = useState(1);
  const softwarePageSize = 25;

  // Parches
  const [parchesSearch, setParchesSearch] = useState('');
  const [parchesSort, setParchesSort] = useState({ field: 'fecha_instalacion', dir: 'desc' });
  const [parchesPage, setParchesPage] = useState(1);
  const parchesPageSize = 25;

  // Servicios
  const [serviciosSearch, setServiciosSearch] = useState('');
  const [serviciosFilter, setServiciosFilter] = useState('all'); // all, running, stopped
  const [serviciosSort, setServiciosSort] = useState({ field: 'service_name', dir: 'asc' });
  const [serviciosPage, setServiciosPage] = useState(1);
  const serviciosPageSize = 25;

  // Roles
  const [rolesSearch, setRolesSearch] = useState('');
  const [rolesSort, setRolesSort] = useState({ field: 'role_name', dir: 'asc' });

  // Sort handler factory
  const createSortHandler = (setter) => (field) => {
    setter(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field, sortConfig }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Generic sort function
  const sortData = (data, sortConfig) => {
    if (!data || !Array.isArray(data)) return [];
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.field] || '';
      let bVal = b[sortConfig.field] || '';
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortConfig.dir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.dir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Filtered and sorted data getters
  const getFilteredSoftware = () => {
    if (!data?.details?.software) return { items: [], total: 0, pages: 0 };
    let filtered = data.details.software.filter(sw => 
      !softwareSearch || 
      sw.name?.toLowerCase().includes(softwareSearch.toLowerCase()) ||
      sw.providername?.toLowerCase().includes(softwareSearch.toLowerCase())
    );
    const sorted = sortData(filtered, softwareSort);
    const total = sorted.length;
    const pages = Math.ceil(total / softwarePageSize);
    const start = (softwarePage - 1) * softwarePageSize;
    return { items: sorted.slice(start, start + softwarePageSize), total, pages };
  };

  const getFilteredParches = () => {
    if (!data?.details?.patches) return { items: [], total: 0, pages: 0 };
    let filtered = data.details.patches.filter(p => 
      !parchesSearch || 
      p.id_parche?.toLowerCase().includes(parchesSearch.toLowerCase()) ||
      p.descripcion?.toLowerCase().includes(parchesSearch.toLowerCase()) ||
      p.kb_titulo?.toLowerCase().includes(parchesSearch.toLowerCase())
    );
    const sorted = sortData(filtered, parchesSort);
    const total = sorted.length;
    const pages = Math.ceil(total / parchesPageSize);
    const start = (parchesPage - 1) * parchesPageSize;
    return { items: sorted.slice(start, start + parchesPageSize), total, pages };
  };

  const getFilteredServicios = () => {
    if (!data?.details?.services) return { items: [], total: 0, pages: 1 };
    let filtered = data.details.services.filter(svc => {
      const matchesSearch = !serviciosSearch || 
        svc.service_name?.toLowerCase().includes(serviciosSearch.toLowerCase()) ||
        svc.display_name?.toLowerCase().includes(serviciosSearch.toLowerCase());
      const matchesFilter = serviciosFilter === 'all' || 
        (serviciosFilter === 'running' && svc.status === '4') ||
        (serviciosFilter === 'stopped' && svc.status === '1');
      return matchesSearch && matchesFilter;
    });
    const sorted = sortData(filtered, serviciosSort);
    const total = sorted.length;
    const pages = Math.ceil(total / serviciosPageSize) || 1;
    const start = (serviciosPage - 1) * serviciosPageSize;
    const items = sorted.slice(start, start + serviciosPageSize);
    return { items, total, pages };
  };

  const getFilteredRoles = () => {
    if (!data?.details?.roles) return [];
    let filtered = data.details.roles.filter(r => 
      !rolesSearch || 
      r.role_name?.toLowerCase().includes(rolesSearch.toLowerCase()) ||
      r.internal_name?.toLowerCase().includes(rolesSearch.toLowerCase())
    );
    return sortData(filtered, rolesSort);
  };

  const fetchServer = async () => {
    try {
      const response = await api.get(`/assets/servers/${hostname}`);
      setData(response.data);
    } catch (error) {
      toast.error('Error al cargar el servidor');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/servers/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  useEffect(() => {
    fetchServer();
    fetchFormOptions();
  }, [hostname]);

  const handleEdit = () => {
    const srv = data.server;
    setFormData({
      hostname: srv.hostname || '',
      vm_name: srv.vm_name || '',
      ip: srv.ip || '',
      ip_mgmt: srv.ip_mgmt || '',
      ip_privada: srv.ip_privada || '',
      description: srv.description || '',
      cpu: srv.cpu || '',
      ram: srv.ram || '',
      marca: srv.marca || '',
      modelo: srv.modelo || '',
      nro_serie: srv.nro_serie || '',
      id_tipo_dispositivo: srv.id_tipo_dispositivo ? String(srv.id_tipo_dispositivo) : '',
      site: srv.site ? String(srv.site) : '',
      os: srv.os ? String(srv.os) : '',
      cloud: srv.cloud ? String(srv.cloud) : '',
      criticity: srv.criticity ? String(srv.criticity) : '',
      farm: srv.farm || '',
      region: srv.region || '',
      // New fields
      restart_window: srv.restart_window ? String(srv.restart_window) : '',
      pki: srv.pki || 0,
      vm_size: srv.vm_size || '',
      ResourceGroup: srv.ResourceGroup || '',
      Location: srv.Location || '',
      costo: srv.costo || '',
      observations: srv.observations || '',
      general_monitoring: srv.general_monitoring || 0,
      services_monitoring: srv.services_monitoring || 0,
    });
    setEditModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.hostname.trim()) {
      toast.error('El hostname es requerido');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        cpu: formData.cpu ? parseInt(formData.cpu) : null,
        ram: formData.ram ? parseInt(formData.ram) : null,
        id_tipo_dispositivo: formData.id_tipo_dispositivo ? parseInt(formData.id_tipo_dispositivo) : null,
        site: formData.site ? parseInt(formData.site) : null,
        os: formData.os ? parseInt(formData.os) : null,
        cloud: formData.cloud ? parseInt(formData.cloud) : null,
        criticity: formData.criticity ? parseInt(formData.criticity) : null,
        // New fields
        restart_window: formData.restart_window ? parseInt(formData.restart_window) : null,
        pki: formData.pki ? 1 : 0,
        general_monitoring: formData.general_monitoring ? 1 : 0,
        services_monitoring: formData.services_monitoring ? 1 : 0,
      };

      Object.keys(payload).forEach(key => {
        if (payload[key] === '') payload[key] = null;
      });

      await api.put(`/assets/servers/${data.server.id}`, payload);
      toast.success('Servidor actualizado exitosamente');
      setEditModalOpen(false);
      
      // Reload data and navigate if hostname changed
      if (formData.hostname !== hostname) {
        navigate(`/assets/servers/${formData.hostname}`);
      } else {
        fetchServer();
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al guardar';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/servers/${data.server.id}`);
      toast.success('Servidor eliminado exitosamente');
      setDeleteModalOpen(false);
      navigate('/assets/servers');
    } catch (error) {
      toast.error('Error al eliminar servidor');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleToggleMonitoring = async (field) => {
    if (!canUpdate || !data?.server) return;
    try {
      const currentValue = data.server[field];
      const payload = { [field]: !currentValue };
      await api.patch(`/assets/servers/${data.server.id}/monitoring`, payload);
      setData(prev => ({
        ...prev,
        server: { ...prev.server, [field]: !currentValue }
      }));
      toast.success('Monitoreo actualizado');
    } catch (error) {
      toast.error('Error al actualizar monitoreo');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="text-center py-12">
          <Server className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Servidor no encontrado</p>
          <Link to="/assets/servers">
            <Button variant="link" className="mt-4 text-primary">Volver al listado</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const { server, details } = data;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/assets/servers">
              <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="page-title flex items-center gap-3">
                <Server className="h-6 w-6 text-primary" />
                {server.hostname}
              </h1>
              <p className="page-subtitle">{server.description || 'Sin descripción'}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {canUpdate && (
              <Button className="btn-nerve btn-nerve-outline" onClick={handleEdit} data-testid="edit-server-btn">
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </Button>
            )}
            {canDelete && (
              <Button className="btn-nerve btn-nerve-danger" onClick={() => setDeleteModalOpen(true)} data-testid="delete-server-btn">
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar
              </Button>
            )}
          </div>
        </div>

        {/* Status Card */}
        <Card className="nerve-card">
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center gap-8">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">IP</p>
                <p className="font-mono text-lg text-primary">{server.ip || '-'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Site</p>
                <p className="text-lg font-medium">{server.site_nombre || '-'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">OS</p>
                <p className="text-lg">{server.os_nombre || '-'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">CPU / RAM</p>
                <p className="font-mono text-lg">
                  {details.hardware?.cores || '-'} cores / {details.hardware?.ram || `${server.ram || '-'} GB`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue={searchParams.get('tab') === 'backups' ? 'backups' : 'general'} className="space-y-4">
          <TabsList className="nerve-card p-1 flex-wrap h-auto gap-1">
            <TabsTrigger value="general" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Cpu className="h-4 w-4 mr-1.5" />
              General
            </TabsTrigger>
            <TabsTrigger value="hardware" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <HardDrive className="h-4 w-4 mr-1.5" />
              Hardware
            </TabsTrigger>
            <TabsTrigger value="roles" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <UserCog className="h-4 w-4 mr-1.5" />
              Roles
            </TabsTrigger>
            <TabsTrigger value="services" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Activity className="h-4 w-4 mr-1.5" />
              Servicios
            </TabsTrigger>
            <TabsTrigger value="software" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Package className="h-4 w-4 mr-1.5" />
              Software
            </TabsTrigger>
            <TabsTrigger value="localadmins" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Users className="h-4 w-4 mr-1.5" />
              Local Admins
            </TabsTrigger>
            <TabsTrigger value="shares" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <FolderOpen className="h-4 w-4 mr-1.5" />
              Shares
            </TabsTrigger>
            <TabsTrigger value="printers" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Printer className="h-4 w-4 mr-1.5" />
              Impresoras
            </TabsTrigger>
            <TabsTrigger value="patches" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Wrench className="h-4 w-4 mr-1.5" />
              Parches
            </TabsTrigger>
            <TabsTrigger value="scheduled_tasks" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <CalendarClock className="h-4 w-4 mr-1.5" />
              Tareas
              {details.scheduled_tasks?.some(t => t.is_red_flag) && (
                <AlertTriangle className="h-3 w-3 ml-1 text-red-500" />
              )}
            </TabsTrigger>
            <TabsTrigger value="backups" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Database className="h-4 w-4 mr-1.5" />
              Backups
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Shield className="h-4 w-4 mr-1.5" />
              Seguridad
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="nerve-card">
                <CardHeader className="border-b border-border pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Settings className="h-4 w-4 text-primary" />
                    Información General
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <DetailRow icon={Monitor} label="Hostname" value={server.hostname} mono highlight />
                  <DetailRow icon={Box} label="VM Name" value={server.vm_name} mono />
                  <DetailRow icon={Building2} label="Marca" value={server.marca} />
                  <DetailRow icon={Tag} label="Modelo" value={server.modelo} />
                  <DetailRow icon={Hash} label="Nro. Serie" value={server.nro_serie} mono />
                  <DetailRow icon={AlertTriangle} label="Criticidad" value={server.criticity_nombre} />
                </CardContent>
              </Card>

              <Card className="nerve-card">
                <CardHeader className="border-b border-border pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Network className="h-4 w-4 text-primary" />
                    Red
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <DetailRow icon={Globe} label="IP Principal" value={server.ip} mono highlight />
                  <DetailRow icon={Wifi} label="IP Gestión" value={server.ip_mgmt} mono />
                  <DetailRow icon={Lock} label="IP Privada" value={server.ip_privada} mono />
                  <DetailRow icon={Cloud} label="Cloud" value={server.cloud_nombre} />
                  <DetailRow icon={Layers} label="Farm" value={server.farm} />
                  <DetailRow icon={MapPin} label="Región" value={server.region} />
                  {server.vm_size && <DetailRow icon={Box} label="VM Size" value={server.vm_size} mono />}
                  {server.ResourceGroup && <DetailRow icon={Box} label="Resource Group" value={server.ResourceGroup} mono />}
                  {server.Location && <DetailRow icon={MapPin} label="Location" value={server.Location} mono />}
                </CardContent>
              </Card>
            </div>

            {/* Operation Section - New */}
            <Card className="nerve-card mt-4">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Settings className="h-4 w-4 text-primary" />
                  Operación
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <DetailRow icon={Clock} label="Ventana Reinicio" value={server.restart_window_nombre} />
                  <DetailRow icon={DollarSign} label="Costo" value={server.costo} />
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">KPI:</span>
                    <span className={`badge ${server.pki ? 'bg-green-500/20 text-green-400' : 'bg-slate-600/30 text-slate-400'}`}>
                      {server.pki ? 'Sí' : 'No'}
                    </span>
                  </div>
                </div>
                {server.observations && (
                  <div className="mt-3 pt-3 border-t border-border/30">
                    <span className="text-sm text-muted-foreground">Observaciones: </span>
                    <span className="text-sm">{server.observations}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Monitoring Section */}
            <Card className="nerve-card mt-4 bg-primary/5 border-primary/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="h-4 w-4 text-primary" />
                  Monitoreo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                    <div>
                      <Label className="text-sm font-medium">Monitoreo General</Label>
                      <p className="text-xs text-muted-foreground">Monitoreo basico de disponibilidad</p>
                    </div>
                    <Switch
                      checked={Boolean(server.general_monitoring)}
                      onCheckedChange={() => handleToggleMonitoring('general_monitoring')}
                      disabled={!canUpdate}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                    <div>
                      <Label className="text-sm font-medium">Monitoreo de Servicios</Label>
                      <p className="text-xs text-muted-foreground">Monitoreo detallado de servicios y metricas</p>
                    </div>
                    <Switch
                      checked={Boolean(server.services_monitoring)}
                      onCheckedChange={() => handleToggleMonitoring('services_monitoring')}
                      disabled={!canUpdate}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="hardware">
            <Card className="nerve-card">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <HardDrive className="h-4 w-4 text-primary" />
                  Especificaciones de Hardware
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {details.hardware ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                      <Cpu className="h-8 w-8 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Cores</p>
                        <p className="font-mono text-xl font-semibold">{details.hardware.cores || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                      <CircuitBoard className="h-8 w-8 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Logical CPU</p>
                        <p className="font-mono text-xl font-semibold">{details.hardware.logicalcpu || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                      <MemoryStick className="h-8 w-8 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">RAM</p>
                        <p className="font-mono text-xl font-semibold">{details.hardware.ram || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                      <HardDrive className="h-8 w-8 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Storage</p>
                        <p className="font-mono text-xl font-semibold">{details.hardware.storage || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                      <Monitor className="h-8 w-8 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">OS Build</p>
                        <p className="font-mono text-sm text-primary">{details.hardware.osversion_full || details.hardware.osversion || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                      <Hash className="h-8 w-8 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Serial Number</p>
                        <p className="font-mono text-sm">{details.hardware.serial_number || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                      <Box className="h-8 w-8 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Model</p>
                        <p className="text-sm font-medium">{details.hardware.model || '-'}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <HardDrive className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Sin información de hardware</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="services">
            <Card className="nerve-card">
              <CardHeader className="border-b border-border pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Activity className="h-4 w-4 text-primary" />
                    Servicios del Sistema ({getFilteredServicios().total})
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar servicio..."
                        value={serviciosSearch}
                        onChange={(e) => { setServiciosSearch(e.target.value); setServiciosPage(1); }}
                        className="pl-8 h-8 w-48 text-sm"
                      />
                    </div>
                    <Select value={serviciosFilter} onValueChange={(v) => { setServiciosFilter(v); setServiciosPage(1); }}>
                      <SelectTrigger className="h-8 w-32 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="running">En ejecución</SelectItem>
                        <SelectItem value="stopped">Detenidos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {getFilteredServicios().items.length > 0 ? (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => { createSortHandler(setServiciosSort)('service_name'); setServiciosPage(1); }}
                          >
                            <div className="flex items-center gap-1">
                              Servicio
                              <SortIcon field="service_name" sortConfig={serviciosSort} />
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => { createSortHandler(setServiciosSort)('display_name'); setServiciosPage(1); }}
                          >
                            <div className="flex items-center gap-1">
                              Display Name
                              <SortIcon field="display_name" sortConfig={serviciosSort} />
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => { createSortHandler(setServiciosSort)('status'); setServiciosPage(1); }}
                          >
                            <div className="flex items-center gap-1">
                              Estado
                              <SortIcon field="status" sortConfig={serviciosSort} />
                            </div>
                          </TableHead>
                          <TableHead>Inicio</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getFilteredServicios().items.map((svc, idx) => {
                          const statusInfo = getServiceStatus(svc.status);
                          const startTypeInfo = getServiceStartType(svc.start_type);
                          return (
                            <TableRow key={idx} className="hover:bg-muted/20">
                              <TableCell className="font-mono text-sm">{svc.service_name}</TableCell>
                              <TableCell className="text-sm">{svc.display_name}</TableCell>
                              <TableCell>
                                <span className={`badge ${statusInfo.badge}`}>
                                  {statusInfo.label}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className={`badge ${startTypeInfo.badge}`}>
                                  {startTypeInfo.label}
                                </span>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    {/* Paginación */}
                    {getFilteredServicios().pages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                        <span className="text-sm text-muted-foreground">
                          Mostrando {((serviciosPage - 1) * serviciosPageSize) + 1} - {Math.min(serviciosPage * serviciosPageSize, getFilteredServicios().total)} de {getFilteredServicios().total}
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setServiciosPage(p => Math.max(1, p - 1))}
                            disabled={serviciosPage === 1}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-sm text-muted-foreground">
                            {serviciosPage} / {getFilteredServicios().pages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setServiciosPage(p => Math.min(getFilteredServicios().pages, p + 1))}
                            disabled={serviciosPage === getFilteredServicios().pages}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      {data?.details?.services?.length > 0 ? 'Sin resultados para el filtro' : 'Sin servicios registrados'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="roles">
            <Card className="nerve-card">
              <CardHeader className="border-b border-border pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Shield className="h-4 w-4 text-primary" />
                    Roles y Características ({getFilteredRoles().length})
                  </CardTitle>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar rol..."
                      value={rolesSearch}
                      onChange={(e) => setRolesSearch(e.target.value)}
                      className="pl-8 h-8 w-48 text-sm"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {getFilteredRoles().length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => createSortHandler(setRolesSort)('role_name')}
                        >
                          <div className="flex items-center gap-1">
                            Rol
                            <SortIcon field="role_name" sortConfig={rolesSort} />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => createSortHandler(setRolesSort)('internal_name')}
                        >
                          <div className="flex items-center gap-1">
                            Nombre Interno
                            <SortIcon field="internal_name" sortConfig={rolesSort} />
                          </div>
                        </TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => createSortHandler(setRolesSort)('install_state')}
                        >
                          <div className="flex items-center gap-1">
                            Estado
                            <SortIcon field="install_state" sortConfig={rolesSort} />
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getFilteredRoles().map((role, idx) => {
                        const installStateInfo = getRoleInstallState(role.install_state);
                        return (
                          <TableRow key={idx} className="hover:bg-muted/20">
                            <TableCell className="text-sm font-medium">{role.role_name}</TableCell>
                            <TableCell className="font-mono text-sm text-muted-foreground">{role.internal_name}</TableCell>
                            <TableCell className="text-sm">{role.feature_type}</TableCell>
                            <TableCell>
                              <span className={`badge ${installStateInfo.badge}`}>
                                {installStateInfo.label}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8">
                    <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      {data?.details?.roles?.length > 0 ? 'Sin resultados para el filtro' : 'Sin roles registrados'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="localadmins">
            <Card className="nerve-card">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <UserCog className="h-4 w-4 text-primary" />
                  Administradores Locales
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {details.localadmins?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead>Usuario</TableHead>
                        <TableHead>Dominio</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {details.localadmins.map((admin, idx) => (
                        <TableRow key={idx} className="hover:bg-muted/20">
                          <TableCell className="font-mono text-sm">{admin.user}</TableCell>
                          <TableCell className="text-sm">{admin.domain || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8">
                    <UserCog className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Sin administradores locales registrados</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="shares">
            <Card className="nerve-card">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <FolderOpen className="h-4 w-4 text-primary" />
                  Carpetas Compartidas
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {details.shares?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead>Nombre</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Comentario</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {details.shares.map((share, idx) => (
                        <TableRow key={idx} className="hover:bg-muted/20">
                          <TableCell className="font-mono text-sm">{share.sharename}</TableCell>
                          <TableCell className="text-sm">{share.type || '-'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{share.comment || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8">
                    <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Sin carpetas compartidas</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="printers">
            <Card className="nerve-card">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Printer className="h-4 w-4 text-primary" />
                  Impresoras
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {details.printers?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead>Impresora</TableHead>
                        <TableHead>Share Name</TableHead>
                        <TableHead>Driver</TableHead>
                        <TableHead>Puerto</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {details.printers.map((printer, idx) => (
                        <TableRow key={idx} className="hover:bg-muted/20">
                          <TableCell className="text-sm font-medium">{printer.printer}</TableCell>
                          <TableCell className="font-mono text-sm">{printer.sharename || '-'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{printer.drivername || '-'}</TableCell>
                          <TableCell className="font-mono text-sm">{printer.port || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8">
                    <Printer className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Sin impresoras configuradas</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="patches">
            <Card className="nerve-card">
              <CardHeader className="border-b border-border pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-primary" />
                    Parches Instalados ({getFilteredParches().total})
                  </CardTitle>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar KB o descripción..."
                      value={parchesSearch}
                      onChange={(e) => { setParchesSearch(e.target.value); setParchesPage(1); }}
                      className="pl-8 h-8 w-56 text-sm"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {getFilteredParches().items.length > 0 ? (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => { createSortHandler(setParchesSort)('id_parche'); setParchesPage(1); }}
                          >
                            <div className="flex items-center gap-1">
                              ID Parche
                              <SortIcon field="id_parche" sortConfig={parchesSort} />
                            </div>
                          </TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Título / Descripción</TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => { createSortHandler(setParchesSort)('fecha_instalacion'); setParchesPage(1); }}
                          >
                            <div className="flex items-center gap-1">
                              Fecha Instalación
                              <SortIcon field="fecha_instalacion" sortConfig={parchesSort} />
                            </div>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getFilteredParches().items.map((patch, idx) => (
                          <TableRow key={idx} className="hover:bg-muted/20">
                            <TableCell className="font-mono text-sm text-primary">{patch.id_parche}</TableCell>
                            <TableCell className="text-sm">
                              <span className={`badge ${
                                patch.kb_tipo?.includes('Security') ? 'badge-error' :
                                patch.kb_tipo?.includes('Cumulative') ? 'badge-warning' :
                                'badge-info'
                              }`}>
                                {patch.kb_tipo || patch.tipo_parche || 'Update'}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground max-w-md truncate" title={patch.kb_titulo || patch.descripcion}>
                              {patch.kb_titulo || patch.descripcion || '-'}
                            </TableCell>
                            <TableCell className="text-sm">{patch.fecha_instalacion || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {getFilteredParches().pages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                        <span className="text-sm text-muted-foreground">
                          {(parchesPage - 1) * parchesPageSize + 1} - {Math.min(parchesPage * parchesPageSize, getFilteredParches().total)} de {getFilteredParches().total}
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline" size="sm"
                            onClick={() => setParchesPage(p => Math.max(1, p - 1))}
                            disabled={parchesPage === 1}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-sm">{parchesPage} / {getFilteredParches().pages}</span>
                          <Button
                            variant="outline" size="sm"
                            onClick={() => setParchesPage(p => Math.min(getFilteredParches().pages, p + 1))}
                            disabled={parchesPage === getFilteredParches().pages}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Wrench className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      {data?.details?.patches?.length > 0 ? 'Sin resultados para el filtro' : 'Sin parches registrados'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ SCHEDULED TASKS TAB ============ */}
          <TabsContent value="scheduled_tasks">
            <ScheduledTasksTab tasks={details.scheduled_tasks || []} />
          </TabsContent>

          <TabsContent value="software">
            <Card className="nerve-card">
              <CardHeader className="border-b border-border pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Package className="h-4 w-4 text-primary" />
                    Software Instalado ({getFilteredSoftware().total})
                  </CardTitle>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar software o proveedor..."
                      value={softwareSearch}
                      onChange={(e) => { setSoftwareSearch(e.target.value); setSoftwarePage(1); }}
                      className="pl-8 h-8 w-56 text-sm"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {getFilteredSoftware().items.length > 0 ? (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => { createSortHandler(setSoftwareSort)('name'); setSoftwarePage(1); }}
                          >
                            <div className="flex items-center gap-1">
                              Software
                              <SortIcon field="name" sortConfig={softwareSort} />
                            </div>
                          </TableHead>
                          <TableHead>Versión</TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => { createSortHandler(setSoftwareSort)('providername'); setSoftwarePage(1); }}
                          >
                            <div className="flex items-center gap-1">
                              Proveedor
                              <SortIcon field="providername" sortConfig={softwareSort} />
                            </div>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getFilteredSoftware().items.map((sw, idx) => (
                          <TableRow key={idx} className="hover:bg-muted/20">
                            <TableCell className="text-sm">{sw.name}</TableCell>
                            <TableCell className="font-mono text-sm">{sw.version}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{sw.providername}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {getFilteredSoftware().pages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                        <span className="text-sm text-muted-foreground">
                          {(softwarePage - 1) * softwarePageSize + 1} - {Math.min(softwarePage * softwarePageSize, getFilteredSoftware().total)} de {getFilteredSoftware().total}
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline" size="sm"
                            onClick={() => setSoftwarePage(p => Math.max(1, p - 1))}
                            disabled={softwarePage === 1}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-sm">{softwarePage} / {getFilteredSoftware().pages}</span>
                          <Button
                            variant="outline" size="sm"
                            onClick={() => setSoftwarePage(p => Math.min(getFilteredSoftware().pages, p + 1))}
                            disabled={softwarePage === getFilteredSoftware().pages}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      {data?.details?.software?.length > 0 ? 'Sin resultados para el filtro' : 'Sin software registrado'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="backups">
            <ServerBackupAssignments serverId={server.id} />
            <Card className="nerve-card">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Database className="h-4 w-4 text-primary" />
                  Registros de backup existentes
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {details.backups?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead>Job Name</TableHead>
                        <TableHead>Habilitado</TableHead>
                        <TableHead>Retención (días)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {details.backups.map((bk, idx) => (
                        <TableRow key={idx} className="hover:bg-muted/20">
                          <TableCell className="font-mono text-sm">{bk.job_name}</TableCell>
                          <TableCell>
                            <span className={`badge ${bk.enabled ? 'badge-success' : 'badge-error'}`}>
                              {bk.enabled ? 'Sí' : 'No'}
                            </span>
                          </TableCell>
                          <TableCell className="font-mono">{bk.keep_days || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8">
                    <Database className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Sin registros de backup existentes</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security">
            <Card className="nerve-card">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Lock className="h-4 w-4 text-primary" />
                  Seguridad
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {details.security?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead>Tipo</TableHead>
                        <TableHead>Producto</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Versión</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {details.security.map((sec, idx) => (
                        <TableRow key={idx} className="hover:bg-muted/20">
                          <TableCell className="text-sm">{sec.tipo_seguridad}</TableCell>
                          <TableCell className="text-sm">{sec.nombre_producto || '-'}</TableCell>
                          <TableCell className="text-sm">{sec.estado || '-'}</TableCell>
                          <TableCell className="font-mono text-sm">{sec.version || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8">
                    <Lock className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Sin información de seguridad</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="modal-content max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-primary" />
                Editar Servidor
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body space-y-6 py-4">
              {/* Basic Info */}
              <div>
                <h4 className="text-sm font-semibold mb-3 text-primary">Información Básica</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Hostname *</Label>
                    <Input 
                      value={formData.hostname || ''} 
                      onChange={(e) => handleInputChange('hostname', e.target.value)} 
                      className="input-subtle mt-1.5" 
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">VM Name</Label>
                    <Input 
                      value={formData.vm_name || ''} 
                      onChange={(e) => handleInputChange('vm_name', e.target.value)} 
                      className="input-subtle mt-1.5"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium">Descripción</Label>
                    <Input 
                      value={formData.description || ''} 
                      onChange={(e) => handleInputChange('description', e.target.value)} 
                      className="input-subtle mt-1.5"
                    />
                  </div>
                </div>
              </div>

              {/* Network */}
              <div>
                <h4 className="text-sm font-semibold mb-3 text-primary">Red</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium">IP Principal</Label>
                    <Input 
                      value={formData.ip || ''} 
                      onChange={(e) => handleInputChange('ip', e.target.value)} 
                      className="input-subtle mt-1.5 font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">IP Gestión</Label>
                    <Input 
                      value={formData.ip_mgmt || ''} 
                      onChange={(e) => handleInputChange('ip_mgmt', e.target.value)} 
                      className="input-subtle mt-1.5 font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">IP Privada</Label>
                    <Input 
                      value={formData.ip_privada || ''} 
                      onChange={(e) => handleInputChange('ip_privada', e.target.value)} 
                      className="input-subtle mt-1.5 font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Hardware */}
              <div>
                <h4 className="text-sm font-semibold mb-3 text-primary">Hardware</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-sm font-medium">CPU (cores)</Label>
                    <Input 
                      type="number"
                      value={formData.cpu || ''} 
                      onChange={(e) => handleInputChange('cpu', e.target.value)} 
                      className="input-subtle mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">RAM (GB)</Label>
                    <Input 
                      type="number"
                      value={formData.ram || ''} 
                      onChange={(e) => handleInputChange('ram', e.target.value)} 
                      className="input-subtle mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Marca</Label>
                    <Input 
                      value={formData.marca || ''} 
                      onChange={(e) => handleInputChange('marca', e.target.value)} 
                      className="input-subtle mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Modelo</Label>
                    <Input 
                      value={formData.modelo || ''} 
                      onChange={(e) => handleInputChange('modelo', e.target.value)} 
                      className="input-subtle mt-1.5"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium">Nro. Serie</Label>
                    <Input 
                      value={formData.nro_serie || ''} 
                      onChange={(e) => handleInputChange('nro_serie', e.target.value)} 
                      className="input-subtle mt-1.5 font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Classification */}
              <div>
                <h4 className="text-sm font-semibold mb-3 text-primary">Clasificación</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Tipo</Label>
                    <Select value={formData.id_tipo_dispositivo || "none"} onValueChange={(v) => handleInputChange('id_tipo_dispositivo', v === "none" ? "" : v)}>
                      <SelectTrigger className="input-subtle mt-1.5">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin seleccionar</SelectItem>
                        {formOptions.tipos?.map(t => (
                          <SelectItem key={t.id} value={String(t.id)}>{t.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Site</Label>
                    <SelectWithCreate
                      value={formData.site}
                      onValueChange={(v) => handleInputChange('site', v)}
                      options={formOptions.sites || []}
                      placeholder="Seleccionar..."
                      createEndpoint="/assets/servers/quick-create/site"
                      entityName="Site"
                      createFields={{
                        name: { label: "Nombre", placeholder: "Ej: TZ_NUEVO" },
                        description: { label: "Descripción", placeholder: "Descripción del site (opcional)" }
                      }}
                      onCreated={(newItem) => {
                        fetchFormOptions();
                        handleInputChange('site', String(newItem.id));
                      }}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Sistema Operativo</Label>
                    <Select value={formData.os || "none"} onValueChange={(v) => handleInputChange('os', v === "none" ? "" : v)}>
                      <SelectTrigger className="input-subtle mt-1.5">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin seleccionar</SelectItem>
                        {formOptions.os?.map(o => (
                          <SelectItem key={o.id} value={String(o.id)}>{o.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Cloud</Label>
                    <Select value={formData.cloud || "none"} onValueChange={(v) => handleInputChange('cloud', v === "none" ? "" : v)}>
                      <SelectTrigger className="input-subtle mt-1.5">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin seleccionar</SelectItem>
                        {formOptions.clouds?.map(c => (
                          <SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Criticidad</Label>
                    <SelectWithCreate
                      value={formData.criticity}
                      onValueChange={(v) => handleInputChange('criticity', v)}
                      options={formOptions.criticities || []}
                      placeholder="Seleccionar..."
                      createEndpoint="/assets/servers/quick-create/criticidad"
                      entityName="Criticidad"
                      createFields={{
                        name: { label: "Tipo", placeholder: "Ej: CRITICO" },
                        description: { label: "Descripción", placeholder: "Descripción (opcional)" }
                      }}
                      onCreated={(newItem) => {
                        fetchFormOptions();
                        handleInputChange('criticity', String(newItem.id));
                      }}
                      className="mt-1.5"
                    />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div>
                <h4 className="text-sm font-semibold mb-3 text-primary">Ubicación</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Farm</Label>
                    <Input 
                      value={formData.farm || ''} 
                      onChange={(e) => handleInputChange('farm', e.target.value)} 
                      className="input-subtle mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Región</Label>
                    <Input 
                      value={formData.region || ''} 
                      onChange={(e) => handleInputChange('region', e.target.value)} 
                      className="input-subtle mt-1.5"
                    />
                  </div>
                </div>
              </div>

              {/* Operation */}
              <div>
                <h4 className="text-sm font-semibold mb-3 text-primary">Operación</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Ventana de Reinicio</Label>
                    <SelectWithCreate
                      value={formData.restart_window}
                      onValueChange={(v) => handleInputChange('restart_window', v)}
                      options={formOptions.restart_windows || []}
                      placeholder="Seleccionar..."
                      createEndpoint="/assets/servers/quick-create/ventana-reinicio"
                      entityName="Ventana de Reinicio"
                      createFields={{
                        name: { label: "Ventana", placeholder: "Ej: Lunes 22:00 - 06:00" },
                        description: { label: "Descripción", placeholder: "Descripción (opcional)" }
                      }}
                      onCreated={(newItem) => {
                        fetchFormOptions();
                        handleInputChange('restart_window', String(newItem.id));
                      }}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Costo</Label>
                    <Input 
                      value={formData.costo || ''} 
                      onChange={(e) => handleInputChange('costo', e.target.value)} 
                      className="input-subtle mt-1.5"
                      placeholder="$1500"
                    />
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <Switch
                      checked={Boolean(formData.pki)}
                      onCheckedChange={(v) => handleInputChange('pki', v ? 1 : 0)}
                    />
                    <Label className="text-sm font-medium">KPI</Label>
                  </div>
                </div>
                <div className="mt-4">
                  <Label className="text-sm font-medium">Observaciones</Label>
                  <Textarea 
                    value={formData.observations || ''} 
                    onChange={(e) => handleInputChange('observations', e.target.value)} 
                    className="input-subtle mt-1.5"
                    placeholder="Notas adicionales..."
                    rows={2}
                  />
                </div>
              </div>

              {/* Monitoring */}
              <div>
                <h4 className="text-sm font-semibold mb-3 text-primary">Monitoreo</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={Boolean(formData.general_monitoring)}
                      onCheckedChange={(v) => handleInputChange('general_monitoring', v ? 1 : 0)}
                    />
                    <Label className="text-sm font-medium">Monitoreo General</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={Boolean(formData.services_monitoring)}
                      onCheckedChange={(v) => handleInputChange('services_monitoring', v ? 1 : 0)}
                    />
                    <Label className="text-sm font-medium">Monitoreo de Servicios</Label>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setEditModalOpen(false)} disabled={saving}>Cancelar</Button>
              <Button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent className="modal-content">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4">
              <p className="text-muted-foreground">
                ¿Está seguro que desea eliminar el servidor <span className="font-semibold text-foreground">{server?.hostname}</span>?
              </p>
              <p className="text-sm text-destructive mt-2">
                Esta acción eliminará también todos los registros relacionados y no se puede deshacer.
              </p>
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>
              <Button className="btn btn-danger" onClick={handleDelete}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ServerDetail;
